import { Navigate, useLocation } from 'react-router-dom';
import { useLayoutEffect } from 'react';
import { clearTokens, getTokens, SESSION_EXPIRED_EVENT } from '@/lib/api';
import { useApp } from '@/context/AppContext';

/** Shared loading UI while auth hydration runs (use in gates that wait on `authHydrated`). */
export function AuthLoadingFallback() {
  return (
    <div className="min-h-[40vh] flex items-center justify-center text-muted-foreground text-sm">
      Memuat sesi…
    </div>
  );
}

function accessTokenLikelyExpired(access) {
  if (!access || typeof access !== 'string') return false;
  const parts = access.split('.');
  if (parts.length < 2) return false;
  try {
    let b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const pad = b64.length % 4;
    if (pad) b64 += '='.repeat(4 - pad);
    const json = atob(b64);
    const p = JSON.parse(json);
    if (typeof p.exp !== 'number') return false;
    return p.exp * 1000 <= Date.now() + 10_000;
  } catch {
    return false;
  }
}

export function ProtectedRoute({ children }) {
  const { authHydrated, user } = useApp();
  const location = useLocation();
  const { access } = getTokens();
  const accessExpired = Boolean(access && accessTokenLikelyExpired(access));

  useLayoutEffect(() => {
    if (!authHydrated || !accessExpired) return;
    clearTokens();
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
    }
  }, [authHydrated, accessExpired, location.pathname]);

  if (!authHydrated) {
    return <AuthLoadingFallback />;
  }

  if (accessExpired) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!access || !user.isLoggedIn || !user.id) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
}
